import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { smsErrorKey } from './smsModel';

/**
 * ТЗ 0081 — ошибки и тосты SMS одним местом.
 *
 * Бизнес-отказ (`customErrors`) формы рисуют под полем; отказ прав и сети —
 * тостом. Перевод — по коду (`sms.errors.<CODE>`), незнакомый код — текст
 * сервера как есть: он лучше пустоты или сырого кода.
 */

export interface ISmsError {
  field: string;
  code: string;
  message: string;
}

export interface ISmsResult {
  ok: boolean;
  errors: ISmsError[];
}

type RawErrors =
  | ReadonlyArray<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

export const toSmsErrors = (raw: RawErrors): ISmsError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));

/** Упавший запрос → одна строка ошибки: нет прав или нет сети. */
export const smsCrashErrors = (caught: unknown): ISmsError[] => {
  const noAccess =
    caught instanceof ApolloError && caught.graphQLErrors.length > 0;
  return [
    {
      field: '',
      code: noAccess ? 'NO_ACCESS' : 'NETWORK',
      message: caught instanceof Error ? caught.message : '',
    },
  ];
};

/**
 * Коды, у которых ответ сервера несёт ФАКТЫ, а не пересказ кода: сколько
 * частей вышло и сколько можно (`TOO_LONG`), чем именно запрещено
 * (`FORBIDDEN`), что мешает прямо сейчас (`BUSY`). Общий перевод у них
 * беднее ответа — он остаётся запасным на случай пустого `message`.
 */
const SERVER_MESSAGE_FIRST = ['TOO_LONG', 'FORBIDDEN', 'BUSY'];

/** Текст одной ошибки: перевод кода, иначе сообщение сервера, иначе код. */
export const useSmsErrorText = () => {
  const { t } = useTranslation();
  return useCallback(
    (error: Partial<ISmsError> | null | undefined): string => {
      if (!error) return '';
      const code = (error.code || '').trim();
      const message = (error.message || '').trim();
      if (message && SERVER_MESSAGE_FIRST.includes(code)) return message;
      const key = smsErrorKey(code);
      if (key) return t(key, { defaultValue: message || code });
      return message || code || t('sms.errors.unknown');
    },
    [t],
  );
};

/** Причина с сервера (код или машинная причина) — тем же правилом. */
export const useSmsErrorsText = () => {
  const one = useSmsErrorText();
  return useCallback(
    (errors: ReadonlyArray<Partial<ISmsError>>): string =>
      errors.map(one).filter(Boolean).join('\n'),
    [one],
  );
};

/** Тост раздела SMS — свой тип: тосты дедуплицируются по типу. */
export const useSmsNotify = () => {
  const { addToast } = useToasts();
  return useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({ data: { type, title, text }, type: GlobalToastTypeNames.Sms });
    },
    [addToast],
  );
};
